import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SeoHead from '@/components/SeoHead';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, BookOpen, PenTool, Users, GraduationCap, School, BarChart3, Shield } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { z } from 'zod';
import MfaChallengeDialog from '@/components/MfaChallengeDialog';
import { ToastAction } from '@/components/ui/toast';
import { getEmailConfirmRedirectUrl } from '@/lib/authRedirect';
import { trackEvent } from '@/lib/analytics';
import { clearSignupPrefill, readSignupPrefill } from '@/lib/signupPrefill';

const RESEND_COOLDOWN_SECONDS = 60;

const emailSchema = z.string().email('Please enter a valid email address');
const passwordSchema = z.string().min(6, 'Password must be at least 6 characters');
const nameSchema = z.string().min(2, 'Name must be at least 2 characters');

export default function Auth() {
  const { user, signIn, signUp, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan') as 'school' | 'individual' | null;
  const { toast } = useToast();

  // Default to sign-up mode when arriving from a CTA with a plan param
  const [isLogin, setIsLogin] = useState(!planParam);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  // Pre-fill from /try ("Start free" / "Sign in"), handed over in sessionStorage, not the URL.
  const [prefill] = useState(() => readSignupPrefill());
  const [email, setEmail] = useState(prefill?.email ?? '');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState(prefill?.firstName ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; name?: string }>({});

  // MFA second-step state — set after successful password auth when user has MFA enrolled
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  // Email awaiting verification — set after sign-up or an "Email not confirmed" sign-in attempt
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (prefill) clearSignupPrefill();
  }, [prefill]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  // Navigate to dashboard once auth is complete (not during pending MFA verification)
  useEffect(() => {
    if (user && !isLoading && !mfaPending) {
      navigate('/dashboard');
    }
  }, [user, isLoading, mfaPending, navigate]);

  const validateForm = () => {
    const newErrors: { email?: string; password?: string; name?: string } = {};

    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      newErrors.email = emailResult.error.errors[0].message;
    }

    const passwordResult = passwordSchema.safeParse(password);
    if (!passwordResult.success) {
      newErrors.password = passwordResult.error.errors[0].message;
    }

    if (!isLogin) {
      const nameResult = nameSchema.safeParse(fullName);
      if (!nameResult.success) {
        newErrors.name = nameResult.error.errors[0].message;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailResult = emailSchema.safeParse(email);
    if (!emailResult.success) {
      setErrors({ email: emailResult.error.errors[0].message });
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setIsSubmitting(false);
    if (error) {
      toast({ title: 'Reset failed', description: error.message, variant: 'destructive' });
    } else {
      toast({
        title: 'Reset email sent',
        description: 'Check your inbox for a password reset link.',
      });
      setIsForgotPassword(false);
    }
  };

  const handleResendVerification = async (targetEmail: string) => {
    if (isResending || resendCooldown > 0) return;
    setIsResending(true);
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: targetEmail,
      options: { emailRedirectTo: getEmailConfirmRedirectUrl() },
    });
    setIsResending(false);
    if (error) {
      toast({ title: "Couldn't resend email", description: error.message, variant: 'destructive' });
      return;
    }
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    toast({
      title: 'Verification email sent',
      description: `We've sent a new link to ${targetEmail}. Check your spam folder if it doesn't arrive in a few minutes.`,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            toast({
              title: 'Login failed',
              description: 'Invalid email or password. Please try again.',
              variant: 'destructive',
            });
          } else if (error.message.includes('Email not confirmed')) {
            const targetEmail = email;
            setUnverifiedEmail(targetEmail);
            toast({
              title: 'Email not verified',
              description: 'Please check your email and click the verification link.',
              variant: 'destructive',
              action: (
                <ToastAction altText="Resend verification email" onClick={() => handleResendVerification(targetEmail)}>
                  Resend email
                </ToastAction>
              ),
            });
          } else {
            toast({
              title: 'Login failed',
              description: error.message,
              variant: 'destructive',
            });
          }
        } else {
          // Check if user has MFA enrolled — if so, pause navigation and show challenge
          const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
          if (aal?.nextLevel === 'aal2') {
            const { data: factors } = await supabase.auth.mfa.listFactors();
            const totpFactor = factors?.totp?.find((f: { status: string }) => f.status === 'verified');
            if (totpFactor) {
              setMfaFactorId(totpFactor.id);
              setMfaPending(true);
            }
          }
        }
      } else {
        const { error } = await signUp(email, password, fullName, planParam ?? undefined, marketingConsent);
        if (error) {
          if (error.message.includes('already registered')) {
            toast({
              title: 'Account exists',
              description: 'An account with this email already exists. Please sign in instead.',
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'Sign up failed',
              description: error.message,
              variant: 'destructive',
            });
          }
        } else {
          // GA4 key event: a completed sign-up form (before email verification).
          trackEvent('sign_up', { method: 'email', plan_type: planParam ?? 'unspecified' });
          setUnverifiedEmail(email);
          toast({
            title: 'Check your email',
            description: 'We sent you a verification link. Please check your inbox.',
          });
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
    <SeoHead
      title="Sign In"
      description="Sign in or create a WriteAssess account."
      canonical="/auth"
      noindex={true}
    />
    <div className="min-h-screen flex bg-background">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-3 text-primary-foreground hover:opacity-80 transition-opacity">
            <div className="h-10 w-10 rounded-xl bg-primary-foreground/20 flex items-center justify-center">
              <PenTool className="h-6 w-6" />
            </div>
            <span className="text-2xl font-bold">WriteAssess</span>
          </Link>
        </div>

        <div className="space-y-6">
          {!isLogin && planParam === 'school' ? (
            <>
              <h1 className="text-4xl font-bold text-primary-foreground leading-tight">
                Whole-school writing assessment, made simple.
              </h1>
              <p className="text-xl text-primary-foreground/80">
                Set up your school once. Invite your teachers. Let the AI do the heavy lifting.
              </p>
              <div className="space-y-4 pt-8">
                {[
                  { icon: <Users className="h-6 w-6" />, title: 'Multi-teacher management', body: 'Invite staff and assign them to classes' },
                  { icon: <BookOpen className="h-6 w-6" />, title: 'School-wide visibility', body: 'Admins see all assessments across every class' },
                  { icon: <Shield className="h-6 w-6" />, title: 'Curriculum-aligned grading', body: 'Best-fit judgements anchored in DfE exemplification' },
                ].map(({ icon, title, body }) => (
                  <div key={title} className="flex items-center gap-4 text-primary-foreground/90">
                    <div className="h-12 w-12 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">{icon}</div>
                    <div><p className="font-semibold">{title}</p><p className="text-sm text-primary-foreground/70">{body}</p></div>
                  </div>
                ))}
              </div>
            </>
          ) : !isLogin && planParam === 'individual' ? (
            <>
              <h1 className="text-4xl font-bold text-primary-foreground leading-tight">
                Your own AI assessment tool — set up in minutes.
              </h1>
              <p className="text-xl text-primary-foreground/80">
                Add your pupils, upload their writing, and get instant curriculum-aligned grades.
              </p>
              <div className="space-y-4 pt-8">
                {[
                  { icon: <PenTool className="h-6 w-6" />, title: 'Instant AI grading', body: 'GPT-6 Luna reads handwriting and grades against the TAF' },
                  { icon: <BarChart3 className="h-6 w-6" />, title: 'Progress over time', body: 'Charts, trajectories, and report comment generation' },
                  { icon: <BookOpen className="h-6 w-6" />, title: 'Ready in 2 minutes', body: 'No IT department needed — just sign up and go' },
                ].map(({ icon, title, body }) => (
                  <div key={title} className="flex items-center gap-4 text-primary-foreground/90">
                    <div className="h-12 w-12 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">{icon}</div>
                    <div><p className="font-semibold">{title}</p><p className="text-sm text-primary-foreground/70">{body}</p></div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold text-primary-foreground leading-tight">
                AI-Powered Writing Assessment for Primary Schools in England
              </h1>
              <p className="text-xl text-primary-foreground/80">
                Help teachers assess pupil writing against National Curriculum standards with confidence.
              </p>
              <div className="space-y-4 pt-8">
                <div className="flex items-center gap-4 text-primary-foreground/90">
                  <div className="h-12 w-12 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                    <BookOpen className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Curriculum-Aligned</p>
                    <p className="text-sm text-primary-foreground/70">Assessments based on the Teacher Assessment Framework</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-primary-foreground/90">
                  <div className="h-12 w-12 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                    <Users className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="font-semibold">Teacher-Friendly</p>
                    <p className="text-sm text-primary-foreground/70">Simple upload and review workflow</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-sm text-primary-foreground/60">
          © {new Date().getFullYear()} WriteAssess. All rights reserved.
        </p>
      </div>

      {/* Right side - Auth form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-8">
        <Card className="w-full max-w-md shadow-soft">
          <CardHeader className="text-center">
            <div className="lg:hidden flex justify-center mb-4">
              <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
                  <PenTool className="h-6 w-6 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold">WriteAssess</span>
              </Link>
            </div>
            {!isLogin && !isForgotPassword && planParam && (
              <div className="flex justify-center mb-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {planParam === 'school' ? (
                    <><School className="h-3.5 w-3.5" /> School plan</>
                  ) : (
                    <><GraduationCap className="h-3.5 w-3.5" /> Individual teacher</>
                  )}
                </span>
              </div>
            )}
            <CardTitle className="text-2xl">
              {isForgotPassword ? 'Reset your password' : isLogin ? 'Welcome back' : 'Create your account'}
            </CardTitle>
            <CardDescription>
              {isForgotPassword
                ? "Enter your email and we'll send you a reset link"
                : isLogin
                ? 'Sign in to access your school dashboard'
                : planParam === 'school'
                ? 'Set up WriteAssess for your whole school'
                : planParam === 'individual'
                ? 'Get started as an individual teacher'
                : 'Get started with WriteAssess today'}
            </CardDescription>
          </CardHeader>

          <form onSubmit={isForgotPassword ? handleForgotPassword : handleSubmit}>
            <CardContent className="space-y-4">
              {!isLogin && !isForgotPassword && (
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={isSubmitting}
                    className={errors.name ? 'border-destructive' : ''}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className={errors.email ? 'border-destructive' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>

              {!isLogin && !isForgotPassword && (
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox
                    id="marketingConsent"
                    checked={marketingConsent}
                    onCheckedChange={(v) => setMarketingConsent(v === true)}
                    disabled={isSubmitting}
                    className="mt-0.5"
                  />
                  <div>
                    <label htmlFor="marketingConsent" className="text-sm text-foreground cursor-pointer leading-snug">
                      Send me occasional product updates, offers and news about WriteAssess.
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      You'll always receive emails about your account and billing. You can change this in Settings at any time.
                    </p>
                  </div>
                </div>
              )}

              {!isForgotPassword && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    {isLogin && (
                      <button
                        type="button"
                        onClick={() => { setIsForgotPassword(true); setErrors({}); }}
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    className={errors.password ? 'border-destructive' : ''}
                  />
                  {errors.password && (
                    <p className="text-sm text-destructive">{errors.password}</p>
                  )}
                </div>
              )}
            </CardContent>

            <CardFooter className="flex flex-col gap-4">
              <Button
                type="submit"
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isForgotPassword ? 'Sending...' : isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isForgotPassword ? 'Send reset link' : isLogin ? 'Sign in' : 'Create account'
                )}
              </Button>

              {unverifiedEmail && !isForgotPassword && (
                <div className="w-full rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground text-center space-y-1">
                  <p>
                    Waiting for you to verify <span className="font-medium text-foreground">{unverifiedEmail}</span>.
                  </p>
                  <p>
                    Didn't get the email?{' '}
                    <button
                      type="button"
                      onClick={() => handleResendVerification(unverifiedEmail)}
                      disabled={isResending || resendCooldown > 0}
                      className="text-primary hover:underline font-medium disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      {isResending
                        ? 'Sending...'
                        : resendCooldown > 0
                          ? `Resend available in ${resendCooldown}s`
                          : 'Resend verification email'}
                    </button>
                  </p>
                </div>
              )}

              {isForgotPassword ? (
                <p className="text-sm text-muted-foreground text-center">
                  <button
                    type="button"
                    onClick={() => { setIsForgotPassword(false); setErrors({}); }}
                    className="text-primary hover:underline font-medium"
                  >
                    Back to sign in
                  </button>
                </p>
              ) : (
                <p className="text-sm text-muted-foreground text-center">
                  {isLogin ? "Don't have an account? " : 'Already have an account? '}
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(!isLogin);
                      setErrors({});
                    }}
                    className="text-primary hover:underline font-medium"
                  >
                    {isLogin ? 'Sign up' : 'Sign in'}
                  </button>
                </p>
              )}
            </CardFooter>
          </form>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-4">
          Need help?{' '}
          <Link to="/help" className="text-primary hover:underline">
            Visit the Help Centre
          </Link>
        </p>
      </div>
    </div>

    {/* MFA second-step challenge — shown after successful password auth */}
    {mfaFactorId && (
      <MfaChallengeDialog
        factorId={mfaFactorId}
        open={mfaPending}
        title="Two-factor authentication"
        description="Enter the 6-digit code from your authenticator app to complete sign-in."
        onSuccess={() => {
          setMfaPending(false);
          navigate('/dashboard');
        }}
        onCancel={() => {
          // Sign out and let user try again — don't leave them half-authenticated
          supabase.auth.signOut().finally(() => {
            setMfaPending(false);
            setMfaFactorId(null);
          });
        }}
      />
    )}
    </>
  );
}
